import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CognitoUserPool, CognitoUserSession } from "amazon-cognito-identity-js";
import { isDemoMode, setTokenProvider } from "./api";

type AuthValue = {
  user: string | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  getToken: () => string | null;
};

const AuthContext = createContext<AuthValue | null>(null);
const allowedDemoUsers = ["Nakul", "Rohit", "Sap"];

let userPoolPromise: Promise<CognitoUserPool | null> | null = null;
function getUserPool() {
  if (isDemoMode || !import.meta.env.VITE_COGNITO_USER_POOL_ID || !import.meta.env.VITE_COGNITO_CLIENT_ID) return Promise.resolve(null);
  if (!userPoolPromise) {
    userPoolPromise = import("amazon-cognito-identity-js").then(({ CognitoUserPool }) => new CognitoUserPool({
      UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID!,
      ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID!,
    }));
  }
  return userPoolPromise;
}

function tokenIsFresh(token: string) {
  try {
    const encodedPayload = token.split(".")[1];
    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as { exp?: number };
    return typeof payload.exp === "number" && payload.exp > Date.now() / 1000 + 30;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);
  const tokenRequestRef = useRef<Promise<string | null> | null>(null);

  const getValidToken = useCallback(async () => {
    if (isDemoMode) return null;
    if (tokenRef.current && tokenIsFresh(tokenRef.current)) return tokenRef.current;
    if (tokenRequestRef.current) return tokenRequestRef.current;

    const tokenRequest = (async () => {
      const pool = await getUserPool();
      const current = pool?.getCurrentUser();
      if (!current) return null;

      return new Promise<string | null>((resolve) => {
        current.getSession((error: Error | null, session: CognitoUserSession | null) => {
          if (error || !session?.isValid()) {
            tokenRef.current = null;
            setToken(null);
            setUser(null);
            resolve(null);
            return;
          }

          const nextToken = session.getIdToken().getJwtToken();
          tokenRef.current = nextToken;
          setToken(nextToken);
          setUser(current.getUsername());
          resolve(nextToken);
        });
      });
    })();

    tokenRequestRef.current = tokenRequest;
    try {
      return await tokenRequest;
    } finally {
      tokenRequestRef.current = null;
    }
  }, []);

  useEffect(() => {
    setTokenProvider(getValidToken);
    return () => setTokenProvider(() => null);
  }, [getValidToken]);

  useEffect(() => {
    if (isDemoMode) {
      setUser(localStorage.getItem("sapk-grocery-demo-user"));
      setLoading(false);
      return;
    }
    getValidToken().finally(() => setLoading(false));
  }, [getValidToken]);

  const signIn = useCallback(async (username: string, password: string) => {
    if (isDemoMode) {
      const matchedUser = allowedDemoUsers.find((name) => name.toLowerCase() === username.trim().toLowerCase());
      if (!matchedUser || password !== "grocery@123") throw new Error("Incorrect username or password.");
      localStorage.setItem("sapk-grocery-demo-user", matchedUser);
      setUser(matchedUser);
      return;
    }
    const pool = await getUserPool();
    if (!pool) throw new Error("Authentication is not configured.");
    const { AuthenticationDetails, CognitoUser } = await import("amazon-cognito-identity-js");
    await new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username.trim(), Pool: pool });
      cognitoUser.authenticateUser(new AuthenticationDetails({ Username: username.trim(), Password: password }), {
        onSuccess: (session) => {
          const nextToken = session.getIdToken().getJwtToken();
          tokenRef.current = nextToken;
          setToken(nextToken);
          setUser(cognitoUser.getUsername());
          resolve();
        },
        onFailure: (error) => reject(new Error(error.message ?? "Sign-in failed.")),
        newPasswordRequired: () => reject(new Error("This account needs a new password. Ask the administrator to finish account setup.")),
      });
    });
  }, []);

  const signOut = useCallback(() => {
    if (isDemoMode) localStorage.removeItem("sapk-grocery-demo-user");
    else getUserPool().then((pool) => pool?.getCurrentUser()?.signOut());
    tokenRef.current = null;
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo<AuthValue>(() => ({ user, loading, signIn, signOut, getToken: () => token }), [user, loading, signIn, signOut, token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
